import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';

export default function ForgotPassword() {
  const navigate = useNavigate();

  // API URL direkt aus Environment oder Fallback
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const [step, setStep] = useState<'email' | 'code' | 'newPassword'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState<any>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Password strength check
  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(strength);
    return strength >= 3;
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'bg-error-500';
      case 2:
        return 'bg-warning-500';
      case 3:
        return 'bg-success-600';
      case 4:
        return 'bg-success-500';
      default:
        return 'bg-error-500';
    }
  };

  // Schritt 1: E-Mail eingeben und Code anfordern
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Direkt beim Backend Code anfordern (ohne Discovery Service)
      console.log('[ForgotPassword] Fordere Reset-Code an für:', email);

      const response = await axios.post(
        `${API_URL}/api/auth/forgot-password-discovery`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-request-id': `web-forgot-${Date.now()}`
          }
        }
      );

      if (response.data.success) {
        setStep('code');
        setError('');

        const modal = {
          visible: true,
          type: 'success' as const,
          title: 'Code gesendet',
          message: 'Ein 6-stelliger Code wurde an Ihre E-Mail-Adresse gesendet. Der Code ist 15 Minuten gültig.',
          variant: 'single' as const,
          showCloseButton: false
        };

        setConfirmModal(modal);
        setTimeout(() => setConfirmModal(null), 3000);
      }
    } catch (err: any) {
      console.error('Fehler beim Password-Reset:', err);

      if (err.response?.data?.error_code === 'RATE_LIMITED') {
        setError('Zu viele Anfragen. Bitte versuchen Sie es in 15 Minuten erneut.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        setError('Server nicht erreichbar. Bitte wenden Sie sich an den Administrator.');
      } else {
        // Aus Sicherheitsgründen: Bei unbekanntem Fehler trotzdem weiter
        setStep('code');
        setError('');

        const modal = {
          visible: true,
          type: 'info' as const,
          title: 'Anfrage bearbeitet',
          message: 'Wenn die E-Mail-Adresse registriert ist, wurde ein Code an Ihre E-Mail gesendet.',
          variant: 'single' as const,
          showCloseButton: false
        };

        setConfirmModal(modal);
        setTimeout(() => setConfirmModal(null), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  // Schritt 2: Code verifizieren und neues Passwort setzen
  const handleVerifyAndReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validierungen
    if (code.length !== 6) {
      setError('Der Code muss 6 Ziffern haben.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    // Backend-konforme Passwort-Validierung
    if (!/^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/.test(newPassword)) {
      setError(
        'Passwort muss mindestens 8 Zeichen lang sein und Großbuchstaben, Zahlen und Sonderzeichen enthalten.'
      );
      return;
    }

    setLoading(true);

    try {
      // Direkt an das Backend senden (ohne Discovery Service)
      const response = await axios.post(
        `${API_URL}/api/auth/reset-password-verify`,
        {
          email,
          code,
          newPassword
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-request-id': `web-reset-${Date.now()}`
          }
        }
      );

      if (response.data.success) {
        // Zeige detailliertes Success Modal mit Hinweisen zu Verschlüsselung
        const modal = {
          visible: true,
          type: 'success' as const,
          title: 'Passwort erfolgreich zurückgesetzt!',
          message: response.data.message || `
            <div class="space-y-3">
              <p class="font-semibold">Ihr Passwort wurde erfolgreich geändert.</p>

              <div class="bg-info-50 dark:bg-info-900/20 rounded-lg p-3 mt-3">
                <p class="text-sm font-medium text-info-800 dark:text-info-200 mb-2">
                  Was passiert jetzt mit Ihren Daten?
                </p>
                <ul class="text-sm text-info-700 dark:text-info-300 space-y-1">
                  <li>• Ein neues Verschlüsselungs-Schlüsselpaar wurde generiert</li>
                  <li>• Alle normalen Funktionen sind sofort verfügbar</li>
                  <li>• Verschlüsselte Dokumente werden vom Vorstand umgeschlüsselt</li>
                  <li>• Sie erhalten eine Benachrichtigung, sobald alle Dokumente wieder verfügbar sind</li>
                </ul>
              </div>

              <p class="text-sm text-gray-600 dark:text-gray-400">
                Sie werden in wenigen Sekunden zum Login weitergeleitet...
              </p>
            </div>
          `,
          variant: 'single' as const,
          showCloseButton: false
        };

        setConfirmModal(modal);

        // Automatisch nach 3 Sekunden zum Login navigieren
        setTimeout(() => {
          setConfirmModal(null);
          navigate('/login', {
            state: {
              email,
              message: 'Passwort erfolgreich zurückgesetzt'
            }
          });
        }, 3000);
      }
    } catch (err: any) {
      console.error('Fehler bei Code-Verifizierung:', err);

      if (err.response?.data?.error === 'Invalid code') {
        setError('Ungültiger Code. Bitte überprüfen Sie Ihre Eingabe.');
      } else if (err.response?.data?.error === 'Code expired') {
        setError('Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Code erneut senden
  const handleResendCode = async () => {
    setStep('email');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <KeyIcon className="h-8 w-8 text-primary-600" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Passwort zurücksetzen
                </h1>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${step === 'email' ? 'text-primary-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'email' ? 'border-primary-600 bg-primary-100 dark:bg-primary-900/30' : 'border-gray-300 dark:border-gray-600'
                }`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium">E-Mail</span>
              </div>

              <div className={`flex-1 h-0.5 mx-2 ${
                step !== 'email' ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`} />

              <div className={`flex items-center ${
                step === 'code' || step === 'newPassword' ? 'text-primary-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'code' || step === 'newPassword' ? 'border-primary-600 bg-primary-100 dark:bg-primary-900/30' : 'border-gray-300 dark:border-gray-600'
                }`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium">Code & Passwort</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-6">
            {step === 'email' && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Code zum Zurücksetzen Ihres Passworts.
                </p>

                {/* Wichtiger Hinweis über Schlüsselverlust */}
                <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-warning-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-warning-800 dark:text-warning-200">
                        Wichtiger Hinweis zum Passwort-Reset
                      </h3>
                      <div className="mt-2 text-sm text-warning-700 dark:text-warning-300">
                        <p>Beim Zurücksetzen Ihres Passworts wird aus Sicherheitsgründen ein neues Verschlüsselungs-Schlüsselpaar generiert.</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Verschlüsselte Dokumente (z.B. Hundedokumente) sind temporär nicht zugänglich</li>
                          <li>Der Vorstand wird automatisch informiert und muss die Dokumente umschlüsseln</li>
                          <li>Dieser Vorgang kann einige Tage dauern</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    E-Mail-Adresse
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-200"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="ihre.email@example.com"
                    />
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-error-100 dark:bg-error-900/30 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sende Code...' : 'Code anfordern'}
                </button>
              </form>
            )}

            {(step === 'code' || step === 'newPassword') && (
              <form onSubmit={handleVerifyAndReset} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Geben Sie den 6-stelligen Code aus Ihrer E-Mail ein und setzen Sie ein neues Passwort.
                </p>

                {/* Erinnerung an Konsequenzen */}
                <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-3 mb-4">
                  <p className="text-xs text-info-700 dark:text-info-300">
                    <strong>Erinnerung:</strong> Nach dem Reset wird ein neues Schlüsselpaar generiert.
                    Verschlüsselte Dokumente müssen vom Vorstand umgeschlüsselt werden.
                  </p>
                </div>

                {/* Code-Eingabe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    6-stelliger Code
                  </label>
                  <input
                    type="text"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-200 text-center text-2xl tracking-widest font-mono"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                    placeholder="000000"
                  />
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Code erneut senden
                  </button>
                </div>

                {/* Neues Passwort */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-200"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        checkPasswordStrength(e.target.value);
                      }}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {newPassword && (
                    <div className="mt-2">
                      <div className="h-1 bg-gray-200 dark:bg-gray-600 rounded">
                        <div
                          className={`h-full rounded transition-all ${getStrengthColor()}`}
                          style={{ width: `${passwordStrength * 25}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Min. 8 Zeichen, Großbuchstabe, Zahl, Sonderzeichen
                      </p>
                    </div>
                  )}
                </div>

                {/* Passwort bestätigen */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Passwort bestätigen
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-200"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-error-100 dark:bg-error-900/30 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !code || !newPassword || !confirmPassword}
                  className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Passwort wird zurückgesetzt...' : 'Passwort zurücksetzen'}
                </button>
              </form>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => navigate('/login')}
                className="w-full text-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Zurück zum Login
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <ConfirmationModal
          visible={confirmModal.visible}
          onClose={() => {
            confirmModal.onClose?.();
            setConfirmModal(null);
          }}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
        />
      )}
    </div>
  );
}
